import type { paths } from '@/api'
import { auth0 } from '@/auth0'
import { API_HOSTNAME, isDev } from '@/config'
import { FeatureFlag } from '@/modules/App/featureFlags'
import { useFeatureFlags } from '@/modules/App/useFeatureFlags'
import { isObject } from '@/shared/utils'
import { captureException, setTag } from '@sentry/vue'
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import axios, { isAxiosError } from 'axios'
import type { APIError, APIResult } from './types'

let instance: AxiosInstance | null = null

const NOT_AUTHENTICATED_ERROR = 'User is not authenticated so cannot make API requests'

export const createClient = () => {
  const baseURL = isDev ? undefined : API_HOSTNAME

  if (!instance) {
    instance = axios.create({ baseURL })

    instance.interceptors.request.use(
      async (config) => {
        if (!auth0.isAuthenticated.value) {
          throw new Error(NOT_AUTHENTICATED_ERROR)
        }

        try {
          const token = await auth0.getAccessTokenSilently()
          // eslint-disable-next-line internal/no-call-composable-outside-setup
          const isExperimentalBackendEnabled = useFeatureFlags(FeatureFlag.EXPERIMENTAL_BACKEND)

          config.headers['Authorization'] = `Bearer ${token}`

          // Add X-V7-Go-Experimental header if the experimental-backend feature flag is enabled
          if (isExperimentalBackendEnabled.value) {
            config.headers['X-V7-Go-Experimental'] = 'true'
          }

          return config
        } catch (err) {
          if (isTokenExpirationError(err)) {
            await auth0.loginWithRedirect()
          }
          throw err
        }
      },
      (error) => {
        return Promise.reject(error)
      },
    )
  }

  return instance
}

createClient()

const isApiError = (e: unknown): e is AxiosError<APIError> => {
  if (isAxiosError(e)) {
    const data = e.response?.data
    return typeof data === 'object' && !!data && 'code' in data && 'message' in data
  }
  return false
}

const BAD_REQUEST_STATUS_CODES = [
  // bad requests, used by backend in similar cases as 422,
  // but usually when the params we sent are outright wrong
  400,
  // forbidden - frontend should have all the information needed to
  // disable/hide UI the user is forbidden from doing
  403,
  // conflict. not really clear when the backend does this, but apparently it can
  409,
  // unsupported media type. usually when the request body is wrong
  // cover endpoints will send this
  415,
]

/**
 * Checks if the error is a bad request.
 *
 * Bad requests are usually when the request body is wrong, or the params are wrong.
 * We should not be sending requests that result in these errors.
 * If we do, it's the app code being wrong, not the user doing something wrong.
 *
 * 5xx are bad, but indicate something wrong on the backend,
 * not frontend, so the backend handles and reports it.
 */
const isBadRequest = (error: AxiosError) =>
  !!error.status && BAD_REQUEST_STATUS_CODES.includes(error.status)

/**
 * This function will be called whenever an API request fails. If the error
 * is returned from the BE in the expected format, it will be returned to the
 * calling function. Otherwise, it throws.
 */
const handleNetworkError = <ResponseType>(error: unknown): APIResult<ResponseType> => {
  if (isApiError(error) && error.response) {
    // Log bad requests to Sentry, otherwise they are quietly gobbled up
    // and we have no way of knowing if we're sending bad requests.
    if (isBadRequest(error)) {
      setTag('api_error_code', error.response.data.code)
      setTag('api_error_message', error.response.data.message)
      setTag('api_error_status', error.status)
      // capture the error grouped by the status and code,
      // so we get a separate issue for each combination
      captureException(new Error(`API error: ${error.status} | ${error.response.data.code}`))
    }
    return { ok: false, error: { ...error.response.data, status: error.status } }
  }

  if (isNotAuthenticatedError(error)) {
    return { ok: false, error: { code: 'login_required', message: 'Login required', status: 401 } }
  }

  if (isGoogleDriveError(error) && error.response) {
    return {
      ok: false,
      error: {
        code: error.response.data.details.token,
        message: 'Google Drive error',
        status: undefined,
      },
    }
  }

  // if it's not an API error, we really don't want to try and handle it
  // as something is probably going wrong
  throw error
}

const isNotAuthenticatedError = (error: unknown) =>
  error instanceof Error && error.message === NOT_AUTHENTICATED_ERROR

function isGoogleDriveError(error: unknown): error is AxiosError<{ details: { token: string } }> {
  if (!isAxiosError(error)) return false
  const data = error.response?.data
  return error instanceof Error && typeof data === 'object' && 'details' in data
}

export const get = async <Params, Response, Path extends string | never = never>(
  path: Path extends string ? Path : keyof paths,
  params: Params,
): Promise<APIResult<Response>> => {
  try {
    const response = await createClient().get<Response>(path, { params })
    return { ok: true, data: response.data }
  } catch (error) {
    return handleNetworkError<Response>(error)
  }
}

type IfEquals<T, U, IfTrue = true, IfFalse = false> = [T] extends [U] ? IfTrue : IfFalse

export const post = async <Params, Response, Path extends string | never = never>(
  path: keyof paths | Path,
  body: IfEquals<Params, never, null, Params>,
  config?: AxiosRequestConfig,
): Promise<APIResult<Response>> => {
  try {
    const response = await createClient().post<Response>(path, body, config)
    return { ok: true, data: response.data }
  } catch (error) {
    return handleNetworkError<Response>(error)
  }
}

export const put = async <Params, Response, Path extends string | never = never>(
  path: keyof paths | Path,
  body: Params,
): Promise<APIResult<Response>> => {
  try {
    const response = await createClient().put<Response>(path, body)
    return { ok: true, data: response.data }
  } catch (error: unknown) {
    return handleNetworkError<Response>(error)
  }
}

export const remove = async <Params, Response, Path extends string | never = never>(
  path: keyof paths | Path,
  body: Params,
): Promise<APIResult<Response>> => {
  try {
    const response = await createClient().delete<Response>(path, { data: body })
    return { ok: true, data: response.data }
  } catch (error) {
    return handleNetworkError<Response>(error)
  }
}

/**
 * Checks if the error is an Auth0 error that indicates
 * that the user needs to login again or refresh the auth token.
 *
 * Auth0 will throw an error with { error: 'login_required' } when
 * auth0.getAccessTokenSilently() is called and previous token has expired.
 * We need to catch this error and call auth0.loginWithRedirect() to get a new token.
 */
function isTokenExpirationError(e: unknown): boolean {
  if (isObject(e, ['error']) && typeof e.error === 'string') {
    return e.error === 'login_required'
  }
  return false
}
